/**
 * Username Setup Modal
 * 
 * Blocking modal that appears when a user connects their wallet
 * but doesn't have a username set. Similar to pump.fun's approach.
 */

import { useState, useEffect } from 'react';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogPortal,
  DialogOverlay,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, RefreshCw, Check, X, Sparkles } from 'lucide-react';
import {
  validateUsername,
  normalizeUsername,
  generateRandomUsername
} from '@/lib/validation/username';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface UsernameSetupModalProps {
  open: boolean;
  walletAddress: string;
  onComplete: (username: string) => Promise<string | null>;
}

export function UsernameSetupModal({
  open,
  walletAddress,
  onComplete,
}: UsernameSetupModalProps) {
  const [username, setUsername] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Generate a random username on mount
  useEffect(() => {
    if (open && walletAddress) {
      const generated = generateRandomUsername(walletAddress);
      setUsername(generated);
      checkAvailability(generated);
    }
  }, [open, walletAddress]);

  // Check username availability
  const checkAvailability = async (usernameToCheck: string) => {
    const normalized = normalizeUsername(usernameToCheck);
    
    // First validate format
    const validation = validateUsername(normalized);
    if (!validation.isValid) {
      setValidationError(validation.error || 'Invalid username');
      setIsAvailable(null);
      return;
    }

    setValidationError(null);
    setIsChecking(true);

    try {
      const { data, error } = await supabase
        .from('creator_profiles')
        .select('username_lower')
        .eq('username_lower', normalized)
        .maybeSingle();

      if (error) {
        console.error('[UsernameSetupModal] Availability check error:', error);
        setValidationError('Could not check availability');
        setIsAvailable(null);
      } else {
        setIsAvailable(!data);
        if (data) {
          setValidationError('This username is taken');
        }
      }
    } catch (err) {
      console.error('[UsernameSetupModal] Availability check error:', err);
      setValidationError('Could not check availability');
      setIsAvailable(null);
    } finally {
      setIsChecking(false);
    }
  };

  // Handle username input change with debounce
  useEffect(() => {
    if (!username) {
      setValidationError(null);
      setIsAvailable(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      checkAvailability(username);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [username]);

  // Generate new random username
  const handleGenerateNew = () => {
    const timestamp = Date.now().toString(36);
    const generated = generateRandomUsername(walletAddress + timestamp);
    setUsername(generated);
  };

  // Handle submit with custom username
  const handleSubmit = async () => {
    if (!isAvailable || validationError || isSubmitting) return;

    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const error = await onComplete(normalizeUsername(username));
      if (error) {
        setSubmitError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const showStatus = username.length > 0 && !isChecking;
  const canSubmit = isAvailable && !validationError && !isSubmitting && !isChecking;

  return (
    <Dialog open={open} onOpenChange={() => {}} modal={!isSubmitting}>
      <DialogPortal>
        <DialogOverlay
          className={cn(isSubmitting && "bg-transparent pointer-events-none z-0")}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg sm:max-w-[400px]",
            isSubmitting && "z-10",
          )}
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Choose your username
            </DialogTitle>
            <DialogDescription>
              A username is required to use dextoro Launch. Pick a unique username for your profile. You can change it later (once every 24 hours).
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="username">Username</Label>
              <div className="relative">
                <Input
                  id="username"
                  placeholder="your_username"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''));
                    setSubmitError(null);
                  }}
                  maxLength={16}
                  className={cn(
                    "pr-10",
                    validationError && "border-destructive",
                    isAvailable === true && "border-green-500"
                  )}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {isChecking && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                  {showStatus && isAvailable === true && <Check className="w-4 h-4 text-green-500" />}
                  {showStatus && isAvailable === false && <X className="w-4 h-4 text-destructive" />}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {username.length}/16 characters
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerateNew}
                  className="h-6 text-xs gap-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  Generate new
                </Button>
              </div>

              {validationError && (
                <p className="text-sm text-destructive">{validationError}</p>
              )}
              
              {isAvailable === true && !validationError && (
                <p className="text-sm text-green-500">Username is available!</p>
              )}
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              <p>• 3-16 characters</p>
              <p>• Lowercase letters, numbers, and underscores only</p>
              <p>• Can be changed once every 24 hours</p>
            </div>
          </div>

          {submitError && (
            <p className="text-sm text-destructive">{submitError}</p>
          )}

          <DialogFooter>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing...
                </>
              ) : (
                'Sign & Confirm'
              )}
            </Button>
          </DialogFooter>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
